import { AxiosRequestConfigMethods } from "@/api/types";
import { brandUrl, fetchFromBrandedUrl } from "@/api/utils";
import { EnvironmentVariables } from "../types";
import {
  FiltersMultiResponse,
  FiltersResponse,
  PageComponentDto,
  QueryPageComponentParams,
} from "../dtos/index.v2";

export const fetchCompoundsLookups = async (searchTerm: string) => {
  const apiUrl = brandUrl<FiltersResponse[]>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/compounds/filter-options`
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: {
        search: searchTerm,
      },
    },
  }) as Promise<{ data: FiltersResponse[] }>;
};

export const fetchDevelopersLookups = async (searchTerm: string) => {
  const apiUrl = brandUrl<FiltersResponse[]>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/developers/filter-options`
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: {
        search: searchTerm,
      },
    },
  }) as Promise<{ data: FiltersResponse[] }>;
};

export const fetchTrackTypesLookups = async ({
  organizationUIds,
}: {
  organizationUIds: string;
}) => {
  const apiUrl = brandUrl<FiltersMultiResponse[]>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/track-types/filter-options`
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: {
        organizationUIds,
      },
    },
  }) as Promise<{ data: FiltersMultiResponse[] }>;
};

export const fetchTrackTypes = async () => {
  const apiUrl = brandUrl<FiltersResponse[]>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/track-types/filter-options`
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
    },
  }) as Promise<{ data: FiltersResponse[] }>;
};

export const fetchTrackStagesLookups = async ({
  search,
  organizationUIds,
}: {
  search: string;
  organizationUIds: string;
}) => {
  const apiUrl = brandUrl<FiltersMultiResponse[]>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/track-stages/filter-options`
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: {
        search,
        organizationUIds,
      },
    },
  }) as Promise<{ data: FiltersMultiResponse[] }>;
};

export const fetchTrackStatusesLookups = async ({
  search,
  organizationUIds,
  trackTypeId,
  leadTypeId,
}: {
  search: string;
  organizationUIds: string;
  trackTypeId?: number;
  leadTypeId?: number;
}) => {
  const apiUrl = brandUrl<FiltersMultiResponse[]>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/statuses/filter-options`
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: {
        search,
        organizationUIds,
        trackTypeId,
        leadTypeId,
      },
    },
  }) as Promise<{ data: FiltersMultiResponse[] }>;
};

export const fetchFunnelsLookups = async (organizationUIds: string) => {
  const apiUrl = brandUrl<FiltersResponse[]>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/funnels/filter-options`
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: {
        organizationUIds,
      },
    },
  }) as Promise<{ data: FiltersResponse[] }>;
};

export const fetchPageComponents = async (params: QueryPageComponentParams) => {
  const apiUrl = brandUrl<PageComponentDto[]>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/components`
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params,
    },
  }) as Promise<{ data: PageComponentDto[] }>;
};

export const fetchAmenitiesLookups = async (searchTerm: string) => {
  const apiUrl = brandUrl<FiltersResponse[]>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/amenities/filter-options`
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: {
        search: searchTerm,
        isPropertyAmenity: true,
      },
    },
  }) as Promise<{ data: FiltersResponse[] }>;
};

export const fetchLeadTypeLookups = async (searchTerm: string) => {
  const apiUrl = brandUrl<FiltersMultiResponse[]>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/lead-types/filter-options`
  );

  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: {
        search: searchTerm,
      },
    },
  }) as Promise<{ data: FiltersMultiResponse[] }>;
};

export const fetchAreasLookups = async (searchTerm: string) => {
  const apiUrl = brandUrl<FiltersResponse[]>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/areas/filter-options`,
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: {
        search: searchTerm,
      },
    },
  }) as Promise<{ data: FiltersResponse[] }>;
};

export const fetchStatusesLookups = async (
  searchTerm: string,
  trackTypeName?: string,
) => {
  const apiUrl = brandUrl<FiltersResponse[]>(
    `${EnvironmentVariables.PROPERTY_MANAGEMENT_API_URL}/statuses/filter-options`,
  );
  return fetchFromBrandedUrl({
    options: {
      brandedUrl: apiUrl,
      isEncoded: false,
      method: AxiosRequestConfigMethods.GET,
      params: {
        search: searchTerm,
        trackTypeName,
      },
    },
  }) as Promise<{ data: FiltersResponse[] }>;
};
